import { yaml as yamlLang } from '@codemirror/lang-yaml';
import { lintGutter } from '@codemirror/lint';
import { githubDark, githubLight } from '@uiw/codemirror-theme-github';
import CodeMirror from '@uiw/react-codemirror';
import { memo, useCallback, useEffect, useRef } from 'react';

import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';

import {
  ScaffolderFieldError,
  ScaffolderFormPaper,
  ScaffolderInputHeader,
} from '../../../components';
import { TEMPLATE_PATCH_INFO } from '../infoDoc';
import { useGeneratorsStyles, useStyles } from '../styles';
import { templatePatchInputErrors } from '../validation';

export interface ApplicationSetTemplatePatchSectionProps {
  value: string;
  onChange: (value: string) => void;
  errors: string[];
}

const ApplicationSetTemplatePatchSectionInner = ({
  value,
  onChange,
  errors,
}: ApplicationSetTemplatePatchSectionProps) => {
  const { classes } = useStyles();
  const { classes: generatorClasses } = useGeneratorsStyles();
  const theme = useTheme();
  const templatePatchError =
    errors.length > 0 ? templatePatchInputErrors(value) : '';
  const sectionRef = useRef<HTMLDivElement>(null);

  // Stable refs — keeps useCallback deps empty ([]) without stale closures.
  // Always read .current inside callbacks instead of closing over the prop directly.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const handleChangeTemplatePatch = useCallback((v: string) => {
    onChangeRef.current(v);
  }, []);

  useEffect(() => {
    if (templatePatchError) {
      sectionRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [templatePatchError]);

  return (
    <div ref={sectionRef}>
      <ScaffolderFormPaper>
        <ScaffolderInputHeader
          title="Template Patch"
          description="Provide a YAML patch to modify the AppSet template used for deploying applications."
          variant="h5"
          divider
          infoDoc={TEMPLATE_PATCH_INFO}
        />
        <Box margin={1} marginBottom={2}>
          <Box className={generatorClasses.codeMirror} sx={{ height: 300 }}>
            <CodeMirror
              value={value || ''}
              extensions={[yamlLang(), lintGutter()]}
              onChange={handleChangeTemplatePatch}
              theme={theme.palette.mode === 'dark' ? githubDark : githubLight}
              placeholder="Enter Template Patch YAML"
            />
          </Box>
          {templatePatchError && (
            <Box className={classes.inputContainer}>
              <ScaffolderFieldError message={templatePatchError} />
            </Box>
          )}
        </Box>
      </ScaffolderFormPaper>
    </div>
  );
};

export const ApplicationSetTemplatePatchSection = memo(
  ApplicationSetTemplatePatchSectionInner,
);
